import React, { useState, useCallback } from 'react';
import DataGrid from '@dataGrid';
import GridConfig from './gridConfig';
import { withStyles } from '@material-ui/core';
import { isTimeValid } from '@dataGrid/gridUtilities';
import MESSAGES from '../../../../../../appMessages';
import { compose } from 'recompose';
import { withSnackbar } from 'notistack';
import GridHeader from './GridHeader';
import { isEmptyDriverPreference, getDefaultDriverPreference } from '../utils';

const styles = (theme) => ({
  dataGrid: {
    minHeight: 150,
    height: 180,
    maxHeight: 240,
    border: `1px solid ${theme.AppBGColor}`,
    '& .ag-theme-material .ag-body-horizontal-scroll': {
      borderWidth: '0px'
    },
    [`&>.gridTheme-${theme.themeName}>.ag-theme-material.theme-${theme.themeName}`]: {
      height: '100%'
    },
    '& .statusBar': {
      display: 'none'
    },
    '& .ag-center-cols-viewport': {
      width: `calc(100% + 18px)`
    },
    '& .ag-theme-material .ag-icon-checkbox-checked': {
      color: theme.HeaderSelectedText
    },
    '& .ag-theme-material .checkBoxHeader': {
      display: 'flex',
      paddingLeft: 24
    }
  }
});

const DriverPreferencesTab = ({
  classes,
  data,
  onFormDataChange,
  enqueueSnackbar
}) => {
  const gridConfig = new GridConfig();
  const [gridApi, setGridApi] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);

  const handleChange = useCallback(
    (newData) => {
      onFormDataChange('groupedDriverPreferencesList', newData);
    },
    [onFormDataChange]
  );

  const onGridReady = useCallback(({ api }) => {
    setGridApi(api);
  }, []);

  const onCellValueChanged = (params) => {
    const isValidUpdate = validateUpdate(params);
    if (!isValidUpdate) {
      return;
    }
    const field = params.colDef.field;
    const newValue = params.newValue;
    
    // Check if the new value is already present in the data other than the current row
    const isDuplicate =
      field === 'startTime' && data?.filter((item) => item.startTime === newValue)?.length > 1;
    if (isDuplicate) {
      handleUpdateError({ params, message: MESSAGES.GROUPED_DRIVER_PREF.STARTTIME_DUPLICATE });
      return;
    }
  };

  const validateUpdate = (params) => {
    if (params.colDef.field === 'startTime') {
      if (params.oldValue !== params.newValue) {
        if (!params.newValue) {
          handleUpdateError({ params, message: MESSAGES.GROUPED_DRIVER_PREF.STARTTIME_EMPTY });
          return false;
        } else if (!isTimeValid(params.newValue)) {
          handleUpdateError({ params, message: MESSAGES.GROUPED_DRIVER_PREF.STARTTIME_INVALID });
          return false;
        }
      } else {
        return false;
      }
    }
    return true;
  };

  const handleUpdateError = ({ params, message }) => {
    params.data[params.colDef.field] = params.oldValue;
    gridApi?.refreshCells();
    enqueueSnackbar(message, { variant: 'error' });
  };

  const onGridSelectionChanged = useCallback(() => {
    if (!gridApi) return;
    const selectedRows = gridApi.getSelectedRows();
    setSelectedRows(selectedRows);
  }, [gridApi]);

  const handleDelete = () => {
    const selectedStartTimes = selectedRows.map((row) => row.startTime);
    const newData = data.filter((item) => !selectedStartTimes.includes(item.startTime));
    handleChange(newData);
  };

  const handleAdd = () => {
    // Only add new row if there's no data or last row is not empty
    if (data.length === 0 || !isEmptyDriverPreference(data[data.length - 1])) {
      const newData = [...data, getDefaultDriverPreference()];
      handleChange(newData);
    }
    enableCellForEditing();
  };

  const enableCellForEditing = () => {
    if (!gridApi) return;

    setTimeout(() => {
      gridApi.forEachNode(({ data, rowIndex }) => {
        if (isEmptyDriverPreference(data)) {
          gridApi.setFocusedCell(rowIndex, 'startTime');
          gridApi.startEditingCell({
            rowIndex,
            colKey: 'startTime'
          });
        }
      });
    }, 100);
  };

  return (
    <div>
      <GridHeader
        handleDelete={handleDelete}
        handleAdd={handleAdd}
        disableDelete={!selectedRows?.length}
      />
      <div className={classes.dataGrid}>
        <DataGrid
          columnDefs={gridConfig?.config?.columns}
          config={gridConfig?.config}
          defaultColDef={gridConfig?.config?.defaultColDef}
          enableFilter={gridConfig?.config?.enableFilter}
          rowSelection={gridConfig?.config?.rowSelection}
          rowData={data}
          onGridReady={onGridReady}
          onCellValueChanged={onCellValueChanged}
          onSelectionChanged={onGridSelectionChanged}
        />
      </div>
    </div>
  );
};

export default compose(withStyles(styles), withSnackbar)(DriverPreferencesTab);
